import { forwardRef, useRef, useState } from "react";
import { toast } from "sonner";
import { downloadPng, renderPng } from "@/lib/media";
import { PCATS, STOCK, bfPublicUrl, campPrice, campRule, exportSrc, fmtDate, fromPrices, giftText, logo, ron } from "@/lib/bf";

const MAX = 4;

export const useCardExport = () => {
  const ref = useRef(null);
  const [item, setItem] = useState(null);
  const run = async (it, filename, quiet = false, share = "") => {
    setItem(it);
    await new Promise((r) => setTimeout(r, 120));
    const node = ref.current;
    await Promise.all([...node.querySelectorAll("img")].map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
    let ok = true;
    try {
      if (share) {
        const url = await renderPng(node);
        const file = new File([await (await fetch(url)).blob()], filename, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], text: share }).catch((e) => { if (e?.name !== "AbortError") throw e; });
        } else {
          const a = document.createElement("a");
          a.href = url; a.download = filename; a.click();
          window.open(`https://wa.me/?text=${encodeURIComponent(share)}`, "_blank", "noopener");
          toast.success("Card descărcat. Atașează-l în WhatsApp din galerie.");
        }
      } else {
        await downloadPng(node, filename);
        if (!quiet) toast.success("Card descărcat");
      }
    } catch {
      ok = false;
      toast.error("Nu am putut genera cardul");
    }
    setItem(null);
    return ok;
  };
  const files = async (list, name) => {
    const out = [];
    for (const it of list) {
      setItem(it);
      await new Promise((r) => setTimeout(r, 120));
      const node = ref.current;
      await Promise.all([...node.querySelectorAll("img")].map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
      const url = await renderPng(node);
      out.push(new File([await (await fetch(url)).blob()], name(it), { type: "image/png" }));
    }
    setItem(null);
    return out;
  };
  return { ref, item, run, files };
};

export const campFile = (c, agent) => `BF_${c.name.replace(/[^\w]+/g, "_")}_${c.brand}${agent?.slug ? `_${agent.slug}` : ""}.png`;

const BFCampaignCard = forwardRef(({ cfg, c, agent }, ref) => {
  const byId = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const offer = c.category === "oferta";
  const items = (offer && c.all_items ? cfg.products.filter((p) => p.brand === c.brand && Number(p.salon) > 0)
    .sort((a, b) => (b.image ? 2 : 0) + (b.shades?.length ? 1 : 0) - (a.image ? 2 : 0) - (a.shades?.length ? 1 : 0)) : c.items.map((id) => byId[id]))
    .filter((p) => p && p.active !== false);
  const cats = offer ? PCATS.map(([k, l]) => [l.split(" /")[0], items.filter((p) => p.cat === k).length]).filter(([, n]) => n) : [];
  const max = cats.length >= 5 ? 2 : offer || fromPrices(c, byId).length ? 3 : MAX;
  const more = items.length - max;
  return (
    <div ref={ref} className={`bfs bfc ${offer ? "bfc--offer" : ""}`} data-testid="bf-campaign-card">
      <div className="bfs-glow" />
      <span className="bfs-kicker">Beauty Trends · ofertă pentru saloane</span>
      <img className="bfc-logo" src={logo(c.brand)} alt="" />
      <p className="bfs-top">Branduri profesionale de top</p>
      <h1>{cfg.title}</h1>
      <p className="bfs-sub">{c.name}</p>
      <div className="bfc-disc">-{c.discount}%</div>
      <div className="bfc-meta">
        {offer && <span data-testid="bf-card-min">{c.surprise_value > 0 ? `Comandă de ${ron(c.min_list + c.surprise_value)} (preț de listă)` : `Comandă de minim ${ron(c.min_list)} (preț de listă)`}</span>}
        {offer && c.surprise_value > 0 && <span>Alegi tu {ron(c.min_list)} + {ron(c.surprise_value)} produse utile alese de noi</span>}
        {offer && <span className="is-gift">Plătești doar {ron(Math.round((c.min_list + (c.surprise_value || 0)) * (100 - c.discount)) / 100)}</span>}
        {!offer && campRule(c) && <span>{campRule(c)}</span>}
        {c.gift && <span className="is-gift">Bonus gratuit: {giftText(c)}</span>}
      </div>
      {!offer && cats.length > 0 && <div className="bfc-from bfc-cats" style={{ gridTemplateColumns: `repeat(${cats.length}, 1fr)` }}>{cats.map(([l, n]) => <span key={l}>{l}<b>{n}</b><small>produse</small></span>)}</div>}
      {!offer && fromPrices(c, byId).length > 0 && (
        <div className="bfc-from">{fromPrices(c, byId).map(([g, v]) => <span key={g}>{g}<small>de la</small><b>{ron(v)}</b></span>)}</div>
      )}
      {!offer && <div className="bfc-list">
        {items.slice(0, max).map((p) => (
          <div key={p.id} className="bfc-row">
            <div className={`bfc-img ${p.image ? "" : "is-empty"}`}><img src={p.image ? exportSrc(p.image) : logo(p.brand)} alt="" crossOrigin="anonymous" /></div>
            <div className="bfc-info">
              <b className="bfc-name">{p.code ? `${p.code} · ` : ""}{p.name}{p.shades?.length ? ` · ${p.shades.length} nuanțe` : ""}</b>
              <div className="bfs-price"><b>{ron(campPrice(p, c))}</b><s>{ron(p.salon)}</s></div>
              {p.retail > 0 && <small>client final {ron(p.retail)}</small>}
            </div>
          </div>
        ))}
        {!items.length && <p className="bfc-empty">Produsele campaniei vor fi anunțate în curând.</p>}
      </div>}
      {offer && <p className="bfc-offer-note" data-testid="bf-card-offer-note">Alegi orice produse {cfg.brands.find((b) => b.id === c.brand)?.name}. Discountul se aplică la toată comanda.</p>}
      {!offer && more > 0 && <p className="bfc-more">+ încă {more} {more === 1 ? "produs" : "produse"} în campanie</p>}
      <div className="bfs-foot">
        <b>{fmtDate(cfg.start)} – {fmtDate(cfg.end)}</b>
        {offer ? (
          <div className="bfc-contact" data-testid="bf-card-contact">
            <small>Comandă la {agent?.name ? "agentul tău" : "Beauty Trends"}</small>
            {agent?.name && <span className="bfc-contact-name" data-testid="bf-card-agent">{agent.name}</span>}
            <b className="bfc-contact-phone" data-testid="bf-card-phone">{agent?.phone || cfg.whatsapp}</b>
          </div>
        ) : <>
        {agent?.name && <span className="bfc-agent" data-testid="bf-card-agent">Agentul tău: {agent.name}</span>}
        <span className="bfc-url" data-testid="bf-card-url">{(bfPublicUrl() + (agent?.slug ? `?a=${agent.slug}` : "") + (offer ? `#b/${c.brand}` : "")).replace(/^https?:\/\//, "")}{(agent?.phone || cfg.whatsapp) ? ` · ${agent?.phone || cfg.whatsapp}` : ""}</span>
        </>}
        <small>Prețuri salon, cu TVA inclus</small>
        <small className="bfc-stock" data-testid="bf-card-stock">{STOCK}</small>
      </div>
    </div>
  );
});

export default BFCampaignCard;
